import {
  BLOCK_TYPES,
  createDefaultBlock,
  newChecklistItemId,
  newFormDocumentBlockId,
  newTableColumnId,
} from "./formDocumentSchema";

const VALID_TYPES = new Set(Object.values(BLOCK_TYPES));

/** Strip optional ```json ... ``` wrapper from pasted text. */
export function stripJsonCodeFence(text) {
  let s = String(text || "").trim();
  if (!s) return s;
  const whole = /^```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```\s*$/i;
  const mWhole = s.match(whole);
  if (mWhole) return mWhole[1].trim();
  const inner = /```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```/i;
  const mInner = s.match(inner);
  if (mInner) return mInner[1].trim();
  return s;
}

function normalizeChecklistItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const label = item.label ?? item.text ?? "";
      if (!String(label).trim()) return null;
      return {
        id: item.id || newChecklistItemId(),
        label: String(label),
      };
    })
    .filter(Boolean);
}

function normalizeTableColumns(columns) {
  if (!Array.isArray(columns)) return [];
  const allowed = new Set(["text", "number", "date", "checkbox"]);
  return columns
    .map((col) => {
      if (!col || typeof col !== "object") return null;
      const label = col.label ?? col.name ?? "";
      if (!String(label).trim()) return null;
      const type = allowed.has(col.type) ? col.type : "text";
      return {
        id: col.id || newTableColumnId(),
        label: String(label),
        type,
      };
    })
    .filter(Boolean);
}

function mergeProps(type, defProps, rawProps) {
  const props = { ...defProps, ...rawProps };

  if (type === BLOCK_TYPES.CHECKLIST) {
    const items = normalizeChecklistItems(rawProps.items ?? defProps.items);
    props.items = items.length ? items : defProps.items;
  }

  if (type === BLOCK_TYPES.TABLE) {
    const columns = normalizeTableColumns(rawProps.columns ?? defProps.columns);
    props.columns = columns.length ? columns : defProps.columns;
    if (rawProps.minRows != null) props.minRows = Math.max(1, Number(rawProps.minRows) || 1);
    if (rawProps.allowAddRows != null) props.allowAddRows = Boolean(rawProps.allowAddRows);
  }

  if (type === BLOCK_TYPES.SELECT) {
    let options = rawProps.options ?? defProps.options;
    if (Array.isArray(options)) {
      props.options = options.map((o) => String(o)).filter(Boolean);
    }
  }

  if (type === BLOCK_TYPES.HEADING && rawProps.level != null) {
    props.level = Math.min(3, Math.max(1, Number(rawProps.level) || 2));
  }

  return props;
}

/** Normalize one block from AI/JSON: merge defaults, assign new id. */
export function normalizeImportedFormDocumentBlock(raw) {
  if (!raw || typeof raw !== "object") return null;
  const type = raw.type;
  if (!VALID_TYPES.has(type)) return null;

  const def = createDefaultBlock(type);
  const id = newFormDocumentBlockId();
  const props = mergeProps(type, def.props || {}, raw.props || {});

  return { id, type, props };
}

/**
 * Parse pasted string into blocks (+ optional title/description).
 * Accepts: raw array, { blocks }, or full template shape.
 */
export function parseImportedFormDocumentJson(text) {
  const trimmed = stripJsonCodeFence(text);
  if (!trimmed) {
    const err = new Error("empty");
    err.code = "empty";
    throw err;
  }

  let data;
  try {
    data = JSON.parse(trimmed);
  } catch {
    const err = new Error("invalid_json");
    err.code = "invalid_json";
    throw err;
  }

  let arr;
  let title;
  let description;

  if (Array.isArray(data)) {
    arr = data;
  } else if (data && Array.isArray(data.blocks)) {
    arr = data.blocks;
    title = data.title;
    description = data.description;
  } else {
    const err = new Error("no_blocks_array");
    err.code = "no_blocks_array";
    throw err;
  }

  const normalized = arr.map((b) => normalizeImportedFormDocumentBlock(b)).filter(Boolean);
  if (normalized.length === 0) {
    const err = new Error("no_valid_blocks");
    err.code = "no_valid_blocks";
    throw err;
  }

  return {
    blocks: normalized,
    title: title != null ? String(title) : undefined,
    description: description != null ? String(description) : undefined,
  };
}

export function serializeFormDocumentForExport(blocks = [], meta = {}) {
  const payload = {
    title: meta.title || "",
    description: meta.description || "",
    blocks: (blocks || []).map(({ id, type, props }) => ({ id, type, props: props || {} })),
  };
  return JSON.stringify(payload, null, 2);
}

/** Minimal example for "insert sample" in the UI */
export const SAMPLE_FORM_DOCUMENT_JSON = `{
  "title": "Interessekonflikt – erklæring",
  "description": "Mal for erklæring om interessekonflikter",
  "blocks": [
    {
      "type": "heading",
      "props": { "text": "Erklæring om interessekonflikt", "level": 1 }
    },
    {
      "type": "paragraph",
      "props": { "content": "<p>Fyll ut skjemaet nedenfor. Obligatoriske felt er merket.</p>" }
    },
    {
      "type": "text_field",
      "props": { "label": "Navn", "placeholder": "Fullt navn", "required": true }
    },
    {
      "type": "checkbox",
      "props": { "label": "Jeg bekrefter at opplysningene er korrekte", "required": true }
    },
    {
      "type": "signature",
      "props": { "label": "Signatur" }
    }
  ]
}`;
